import { Button } from '@design/components/button/Button';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { SmartTableColumn, SmartTableDataItem } from '@design/components/smart-table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SharedTable } from '../shared-table/SharedTable';
import styles from './SectionBase.module.scss';

export interface SectionBaseProps<T> {
  title: string;
  onExpand?: () => void;
  tableData?: SmartTableDataItem<T>[];
  tableColumns?: SmartTableColumn<T>[];
  isFetching: boolean;
  onRowClick?: (item: SmartTableDataItem<T>) => void;
  hideExpandButton?: boolean;
}

export const SectionBase = <T extends {}>({
  title,
  tableData = [],
  tableColumns = [],
  onExpand,
  isFetching,
  onRowClick,
  hideExpandButton = false,
}: SectionBaseProps<T>) => {
  const { t } = useTranslation(['insights']);

  return (
    <div className={styles.root}>
      <div className={styles.headerWrapper}>
        <div className={styles.title}>{title}</div>
        {!hideExpandButton && (
          <Tooltip
            label={t('insights:viewMore')}
            inPortal
            trigger={[TooltipTrigger.Hover]}
          >
            <Button
              variant="tertiary"
              iconName="Expand"
              size="small"
              onClick={() => onExpand?.()}
            />
          </Tooltip>
        )}
      </div>
      {isFetching ? (
        <div className={styles.loadingSkeletonWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '96px', borderRadius: '8px' },
              },
            ]}
          />
        </div>
      ) : (
        <SharedTable
          tableData={tableData}
          tableColumns={tableColumns}
          onRowClick={onRowClick}
        />
      )}
    </div>
  );
};
